import { type ChangeEvent, type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { cx } from '@/styled-system/css';
import controller from 'app/shared/modules/hdp/form/Contact/controller';
import contactUtils from 'app/shared/utils/contactUtils';
import numberUtils from 'app/shared/utils/numberUtils';
import { listingUtils_getContactEmail } from 'app/shared/utils/listingUtils';
import { mailcheck } from 'app/shared/utils/mailcheckConfig';
import dateUtils from 'app/shared/utils/dateUtils';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { ContactFormInputErrors, ContactFormInputValues, ContactFormMode, ContactFormServerErrors } from './types';
import * as styles from './contactForm.styles';

type ContactFormProps = {
  listing: ListingDetails;
  onSubmit: (values: ContactFormInputValues) => void;
  isLoading: boolean;
  serverErrors: ContactFormServerErrors;
  userInquiryDataCache?: {
    text?: string | null;
    name?: string | null;
    phone?: string | null;
    email?: string | null;
  };
  user?: {
    loggedIn?: boolean;
    info?: {
      firstName?: string | null;
      lastName?: string | null;
    };
  };
  mode?: ContactFormMode;
  idPrefix?: string;
};

export const ContactForm = ({
  listing,
  onSubmit,
  isLoading,
  serverErrors,
  userInquiryDataCache,
  user,
  mode = 'contact',
  idPrefix = 'halo-contact',
}: ContactFormProps) => {
  const firstErrorRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null);

  // Derive initial values
  const userEmail = listingUtils_getContactEmail(user, userInquiryDataCache) || '';
  const userInfoName =
    user?.info?.firstName && user?.info?.lastName ? `${user.info.firstName} ${user.info.lastName}` : '';

  let defaultMessage = userInquiryDataCache?.text || listing?.contact?.defaultInquiryMessage || '';
  if (listing?.waitlisted) {
    defaultMessage = 'Please add me to the waitlist for this listing. Thank you.';
  } else if (mode === 'requestToApply' && !userInquiryDataCache?.text) {
    defaultMessage =
      "I'm interested in submitting an application for this rental. Please let me know the next steps to apply.";
  }

  const dedupedFloorPlans = controller.dedupeBedsBathsOptions(listing?.floorplans || []);
  const defaultBeds = dedupedFloorPlans[0]?.beds ?? '';
  const defaultBaths = dedupedFloorPlans[0]?.baths ?? '';

  const [inputValues, setInputValues] = useState<ContactFormInputValues>({
    userMessage: defaultMessage,
    userEmail,
    userName: userInquiryDataCache?.name || userInfoName,
    userPhoneNumber: numberUtils.cleanPhoneNumber(userInquiryDataCache?.phone || ''),
    numBeds: String(defaultBeds),
    numBaths: String(defaultBaths),
    moveDate: dateUtils.formatDateMmddyyyy(new Date()),
    lowIncomeMessage: null,
  });

  const [inputErrors, setInputErrors] = useState<ContactFormInputErrors>({
    userMessage: null,
    userEmail: controller.userHasMaskedEmail(userEmail) ? 'Please update your email address.' : null,
    userName: null,
    userPhoneNumber: null,
    numBeds: null,
    numBaths: null,
    moveDate: null,
  });

  const [restrictedConfirmation, setRestrictedConfirmation] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [mailcheckHint, setMailcheckHint] = useState('');

  const maskedEmail = controller.userHasMaskedEmail(inputValues.userEmail);
  const { contact } = listing;
  const requiresIncomeChoice = contactUtils.shouldRequireRestrictedIncomeChoice(listing);
  const isRestricted = listing.contact.restricted || listing.details?.studentHousing || listing.details?.seniorHousing;

  const handleFieldChange = useCallback(
    (field: keyof ContactFormInputValues) =>
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setInputValues((prev) => ({ ...prev, [field]: e.target.value }));
        // Clear field error on change
        if (field in inputErrors) {
          setInputErrors((prev) => ({ ...prev, [field]: null }));
        }
      },
    [inputErrors],
  );

  const handlePhoneChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const raw = numberUtils.cleanPhoneNumber(e.target.value);
    setInputValues((prev) => ({ ...prev, userPhoneNumber: raw }));
    setInputErrors((prev) => ({ ...prev, userPhoneNumber: null }));
  }, []);

  const handleEmailBlur = useCallback(() => {
    if (controller.userHasMaskedEmail(inputValues.userEmail)) {
      setInputErrors((prev) => ({ ...prev, userEmail: 'Please enter a valid email address.' }));
    }

    mailcheck.run({
      email: inputValues.userEmail,
      suggested: (suggestion: { full: string }) => {
        setMailcheckHint(`Did you mean ${suggestion.full}?`);
      },
      empty: () => {
        setMailcheckHint('');
      },
    });
  }, [inputValues.userEmail]);

  const handleMailcheckAccept = useCallback(() => {
    // Extract the suggested email from the hint text
    const match = mailcheckHint.match(/Did you mean (.+)\?/);
    if (match?.[1]) {
      setInputValues((prev) => ({ ...prev, userEmail: match[1] }));
      setMailcheckHint('');
    }
  }, [mailcheckHint]);

  // Focus the first error field when validation fails
  useEffect(() => {
    if (firstErrorRef.current) {
      firstErrorRef.current.focus();
      firstErrorRef.current = null;
    }
  });

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      // Client-side validation
      const { errorObj, isValid } = controller.validateContactFormInput({
        inputValues,
        listing,
        restrictedConfirmation,
      });

      setInputErrors(errorObj.inputErrors);
      setFormError(errorObj.formError);

      if (!isValid) {
        // Find first error field and focus it
        const errorFields = errorObj.inputErrors;
        const fieldOrder: Array<keyof ContactFormInputErrors> = [
          'userMessage',
          'userName',
          'userEmail',
          'userPhoneNumber',
          'numBeds',
          'numBaths',
          'moveDate',
        ];
        for (const field of fieldOrder) {
          if (errorFields[field]) {
            const el = document.getElementById(`${idPrefix}-${field}`);
            if (el) {
              (el as HTMLInputElement).focus();
            }
            break;
          }
        }
        return;
      }

      onSubmit(inputValues);
    },
    [inputValues, listing, restrictedConfirmation, onSubmit, idPrefix],
  );

  return (
    <form id={`${idPrefix}-form`} onSubmit={handleSubmit} className={styles.formContainer} noValidate>
      {/* Income restrictions */}
      {requiresIncomeChoice && (
        <fieldset className={styles.fieldsetGroup}>
          <legend className={styles.legendText}>Housing requirements</legend>
          <div className={styles.fieldGroup}>
            <label htmlFor={`${idPrefix}-lowIncomeMessage`} className={styles.fieldLabel}>
              Number of residents and income range
              <span className={styles.requiredIndicator} aria-hidden="true">
                *
              </span>
            </label>
            <select
              id={`${idPrefix}-lowIncomeMessage`}
              className={styles.selectInput}
              value={inputValues.lowIncomeMessage || ''}
              onChange={(e) => setInputValues((prev) => ({ ...prev, lowIncomeMessage: e.target.value || null }))}
              aria-required="true"
            >
              <option value="">Select...</option>
              {listing.contact.restrictedIncomeByOccupants &&
                Object.entries(listing.contact.restrictedIncomeByOccupants)
                  .filter(([, restriction]) => restriction !== '')
                  .map(([persons, restriction]) => (
                    <option
                      key={persons}
                      value={`I am a ${Number(persons) === 1 ? 'single person' : `household of ${persons}`} with an annual income below ${restriction}.`}
                    >
                      {Number(persons)} {Number(persons) === 1 ? 'resident' : 'residents'} — income below {restriction}
                    </option>
                  ))}
            </select>
          </div>
        </fieldset>
      )}

      {/* Restricted confirmation (non-income restrictions) */}
      {!requiresIncomeChoice && isRestricted && (
        <div className={styles.checkboxRow}>
          <input
            type="checkbox"
            id={`${idPrefix}-restricted`}
            className={styles.checkboxInput}
            checked={restrictedConfirmation}
            onChange={(e) => setRestrictedConfirmation(e.target.checked)}
            aria-required="true"
          />
          <label htmlFor={`${idPrefix}-restricted`} className={styles.checkboxLabel}>
            I confirm I meet the housing requirements for this listing.
          </label>
        </div>
      )}

      {/* Move-in date */}
      {contact.requiresMoveDate && (
        <div className={styles.fieldGroup}>
          <label htmlFor={`${idPrefix}-moveDate`} className={styles.fieldLabel}>
            Move-in date
            <span className={styles.requiredIndicator} aria-hidden="true">
              *
            </span>
          </label>
          <input
            type="date"
            id={`${idPrefix}-moveDate`}
            className={cx(styles.textInput, inputErrors.moveDate ? styles.textInputError : '')}
            value={inputValues.moveDate}
            onChange={handleFieldChange('moveDate')}
            aria-required="true"
            aria-invalid={!!inputErrors.moveDate}
            aria-describedby={inputErrors.moveDate ? `${idPrefix}-moveDate-error` : undefined}
          />
          {inputErrors.moveDate && (
            <span id={`${idPrefix}-moveDate-error`} className={styles.fieldError} role="alert">
              {inputErrors.moveDate}
            </span>
          )}
        </div>
      )}

      {/* Beds/Baths */}
      {contact.requiresNumBeds && (
        <fieldset className={styles.fieldsetGroup}>
          <legend className={styles.legendText}>Unit preferences</legend>
          <div className={styles.bedsAndBathsRow}>
            <div className={styles.fieldGroup}>
              <label htmlFor={`${idPrefix}-numBeds`} className={styles.fieldLabel}>
                Bedrooms
                <span className={styles.requiredIndicator} aria-hidden="true">
                  *
                </span>
              </label>
              <select
                id={`${idPrefix}-numBeds`}
                className={styles.selectInput}
                value={inputValues.numBeds}
                onChange={handleFieldChange('numBeds')}
                aria-required="true"
                aria-invalid={!!inputErrors.numBeds}
                aria-describedby={inputErrors.numBeds ? `${idPrefix}-numBeds-error` : undefined}
              >
                <option value="">Select beds</option>
                {dedupedFloorPlans.map((fp: { beds: string; baths: string; key: string }) => (
                  <option key={fp.key} value={fp.beds}>
                    {fp.key}
                  </option>
                ))}
              </select>
              {inputErrors.numBeds && (
                <span id={`${idPrefix}-numBeds-error`} className={styles.fieldError} role="alert">
                  {inputErrors.numBeds}
                </span>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor={`${idPrefix}-numBaths`} className={styles.fieldLabel}>
                Bathrooms
                <span className={styles.requiredIndicator} aria-hidden="true">
                  *
                </span>
              </label>
              <select
                id={`${idPrefix}-numBaths`}
                className={styles.selectInput}
                value={inputValues.numBaths}
                onChange={handleFieldChange('numBaths')}
                aria-required="true"
                aria-invalid={!!inputErrors.numBaths}
                aria-describedby={inputErrors.numBaths ? `${idPrefix}-numBaths-error` : undefined}
              >
                <option value="">Select baths</option>
                {dedupedFloorPlans.map((fp: { beds: string; baths: string; key: string }) => (
                  <option key={`bath-${fp.key}`} value={fp.baths}>
                    {fp.baths} bath
                  </option>
                ))}
              </select>
              {inputErrors.numBaths && (
                <span id={`${idPrefix}-numBaths-error`} className={styles.fieldError} role="alert">
                  {inputErrors.numBaths}
                </span>
              )}
            </div>
          </div>
        </fieldset>
      )}

      {/* Message */}
      <div className={styles.fieldGroup}>
        <label htmlFor={`${idPrefix}-userMessage`} className={styles.fieldLabel}>
          Message
          {contact.requiresMessage && (
            <span className={styles.requiredIndicator} aria-hidden="true">
              *
            </span>
          )}
        </label>
        <textarea
          id={`${idPrefix}-userMessage`}
          className={cx(styles.textarea, inputErrors.userMessage ? styles.textInputError : '')}
          value={inputValues.userMessage}
          onChange={handleFieldChange('userMessage')}
          maxLength={750}
          aria-required={contact.requiresMessage}
          aria-invalid={!!inputErrors.userMessage}
          aria-describedby={inputErrors.userMessage ? `${idPrefix}-userMessage-error` : undefined}
        />
        {inputErrors.userMessage && (
          <span id={`${idPrefix}-userMessage-error`} className={styles.fieldError} role="alert">
            {inputErrors.userMessage}
          </span>
        )}
      </div>

      {/* Name */}
      <div className={styles.fieldGroup}>
        <label htmlFor={`${idPrefix}-userName`} className={styles.fieldLabel}>
          Your name
          {contact.requiresName && (
            <span className={styles.requiredIndicator} aria-hidden="true">
              *
            </span>
          )}
        </label>
        <input
          type="text"
          id={`${idPrefix}-userName`}
          className={cx(styles.textInput, inputErrors.userName ? styles.textInputError : '')}
          value={inputValues.userName}
          onChange={handleFieldChange('userName')}
          maxLength={50}
          autoComplete="name"
          aria-required={contact.requiresName}
          aria-invalid={!!inputErrors.userName}
          aria-describedby={inputErrors.userName ? `${idPrefix}-userName-error` : undefined}
        />
        {inputErrors.userName && (
          <span id={`${idPrefix}-userName-error`} className={styles.fieldError} role="alert">
            {inputErrors.userName}
          </span>
        )}
      </div>

      {/* Email + Phone (side-by-side) */}
      <div className={styles.emailPhoneRow}>
        <div className={styles.fieldGroup}>
          <label htmlFor={`${idPrefix}-userEmail`} className={styles.fieldLabel}>
            Email
            {contact.requiresEmail && (
              <span className={styles.requiredIndicator} aria-hidden="true">
                *
              </span>
            )}
          </label>
          <input
            type="email"
            id={`${idPrefix}-userEmail`}
            className={cx(
              styles.textInput,
              inputErrors.userEmail || serverErrors.emailError ? styles.textInputError : '',
            )}
            value={inputValues.userEmail}
            onChange={handleFieldChange('userEmail')}
            onBlur={handleEmailBlur}
            maxLength={50}
            autoComplete="email"
            aria-required={contact.requiresEmail}
            aria-invalid={!!(inputErrors.userEmail || serverErrors.emailError)}
            aria-describedby={
              inputErrors.userEmail || serverErrors.emailError ? `${idPrefix}-userEmail-error` : undefined
            }
          />
          {(inputErrors.userEmail || serverErrors.emailError) && (
            <span id={`${idPrefix}-userEmail-error`} className={styles.fieldError} role="alert">
              {inputErrors.userEmail || serverErrors.emailError}
            </span>
          )}
          {mailcheckHint && !inputErrors.userEmail && (
            <button type="button" className={styles.mailcheckHintButton} onClick={handleMailcheckAccept}>
              {mailcheckHint}
            </button>
          )}
        </div>

        <div className={styles.fieldGroup}>
          <label htmlFor={`${idPrefix}-userPhoneNumber`} className={styles.fieldLabel}>
            Phone
            {contact.requiresPhone && (
              <span className={styles.requiredIndicator} aria-hidden="true">
                *
              </span>
            )}
          </label>
          <input
            type="tel"
            id={`${idPrefix}-userPhoneNumber`}
            className={cx(
              styles.textInput,
              inputErrors.userPhoneNumber || serverErrors.phoneError ? styles.textInputError : '',
            )}
            value={numberUtils.formatPhoneNumber(inputValues.userPhoneNumber)}
            onChange={handlePhoneChange}
            maxLength={14}
            placeholder="(123) 456-7890"
            autoComplete="tel"
            aria-required={contact.requiresPhone}
            aria-invalid={!!(inputErrors.userPhoneNumber || serverErrors.phoneError)}
            aria-describedby={
              inputErrors.userPhoneNumber || serverErrors.phoneError ? `${idPrefix}-userPhoneNumber-error` : undefined
            }
          />
          {(inputErrors.userPhoneNumber || serverErrors.phoneError) && (
            <span id={`${idPrefix}-userPhoneNumber-error`} className={styles.fieldError} role="alert">
              {inputErrors.userPhoneNumber || serverErrors.phoneError}
            </span>
          )}
        </div>
      </div>

      {/* Form-level error */}
      {(formError || serverErrors.formError) && (
        <div className={styles.formError} role="alert">
          {formError || serverErrors.formError}
        </div>
      )}

      {/* Loading status for screen readers */}
      {isLoading && (
        <div className={styles.srOnly} role="status" aria-live="polite">
          Sending your message...
        </div>
      )}
    </form>
  );
};
